import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";

import SelectionMainList from "../../selectionWindow/selectionMainList";
import {getListItemHeight} from "../materialsCommon/materialsCommonFunctions";
import {setWizardSelectedValues} from "../../../actions/wizardFunctions";
import {browserIsSafari} from "../../../misc/helpers";
import {updateNavigationCount} from "../../../actions/lookFunctions";
import {wizardGroupInterface} from "../../../interfaces/wizardItemInterface";
import {useIsMounted} from "../../../misc/helperHooks";
import {
  getWizardMissingKey,
  shouldUseNewWizardFeaturesRequestsLogic
} from "../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {shouldSwitchVisualizer, switchToNewVisualizerType} from "../../../misc/visualizerSwitchingFunctions";
import {UserPopupTypes} from "../../visComponents/constants";
import Analytics from "../../../analytics";
import {toggleUserPopup} from "../../../actions/actionFunctions";
import {fixFeature} from "../../sqft/components/popup/modalFix/CompatibilityWarnings/modalFixHelper";


const MaterialsWizardMainList = (props) => {

  const {setRef, currentParent, features, layoutName, selectionWindowView, isMobile, setWizardSelectedValues, bvOpened,
    wizardActiveStep, wizardGroup, updateNavigationCount, currentGroups, wizardMissingFeatures, fabricatorSettings,
    visualizerIsChanging, isPhoneNumberValidated, userIsLoggedIn, toggleUserPopup, wizardActiveItem} = props;

  const [resizing, setResizing] = useState(false);

  const itemsList = useRef();
  const resizeId = useRef();
  const prevItemHeight = useRef();
  const isMounted = useIsMounted();

  const itemHeight = useMemo(() => {
    if (resizing && prevItemHeight.current) {
      return prevItemHeight.current
    }
    const height = isMobile && itemsList.current
      ? itemsList.current.clientHeight
      : getListItemHeight({selectionWindowView, isMobile, itemsList});
    prevItemHeight.current = height;
    return height
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionWindowView, isMobile, itemsList.current, resizing]);

  const updateHeight = () => {
    if (!isMounted()) {
      return
    }
    setResizing(false);
  }

  const onResize = () => {
    if (!resizeId || window.inputIsFocused) {
      return
    }
    setResizing(true);
    clearTimeout(resizeId.current);
    resizeId.current = window.setTimeout(updateHeight, 300);
  }

  useEffect(() => {
    window.addEventListener("resize", onResize);
    if (browserIsSafari()) {
      window.addEventListener('scroll', onResize);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (browserIsSafari()) {
        window.removeEventListener('scroll', onResize);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // console.log("resizing", resizing);
  // console.log("itemHeight", itemHeight);

  useEffect(() => {
    if (!itemHeight) {
      window.dispatchEvent(new Event("resize")) // after container is mounted - rerun the height calculations
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bvOpened]);

  const handleFeatureClick = useCallback(async (feature) => {
    if (shouldSwitchVisualizer(feature, "", fabricatorSettings, visualizerIsChanging)) {
      return false
    }
    setWizardSelectedValues({
      [wizardActiveStep]: {
        wizardGroup,
        selectedFeature: feature,
        partId: feature.partId,
      }
    });
    await fixFeature(feature, {
      fromWizardId: wizardActiveItem.id,
      fromWizardStep: wizardActiveStep,
      fromWizardGroupId: wizardGroup.id
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setWizardSelectedValues, wizardActiveStep, wizardGroup]);

  const checkIfNeedToSwitchRoom = async (featureClicked) => {
    const newVisualizer = shouldSwitchVisualizer(featureClicked, "", fabricatorSettings, visualizerIsChanging);
    if (newVisualizer) {
      await switchToNewVisualizerType(newVisualizer);
    }
  }

  const handleUserLoginClick = (userPopupType = UserPopupTypes.Unlock) => {
    const getQuoteEnabled = fabricatorSettings.enableQuote;
    Analytics.sendClickEvent(getQuoteEnabled ? 'get-quote-feature-selection' : 'user-login');
    if (userIsLoggedIn) {
      if (fabricatorSettings.twoFA) {
        if (!isPhoneNumberValidated) {
          toggleUserPopup({
            userPopupOpened: true,
            userPopupType
          });
        }
      }
    } else {
      toggleUserPopup({
        userPopupOpened: true,
        userPopupType
      });
    }
  };

  const getContent = () => {
    // console.log("wizardMissingFeatures", wizardMissingFeatures);

    let useNewWizardFeaturesRequestsLogic = shouldUseNewWizardFeaturesRequestsLogic(wizardGroup, currentParent);
    const alreadyRequestedWizardFeatures = !!wizardMissingFeatures[getWizardMissingKey(wizardActiveItem, wizardGroup)]; // if it is an array - disable loader
    if (useNewWizardFeaturesRequestsLogic) {
      if (!alreadyRequestedWizardFeatures) {
        return <div className="message-loading-left-panel-spinner message-loading-left-panel-spinner--small"/>
      }
    // } else if (currentParent && !currentParent.allFeaturesReceived) {
    //   return <div className="message-loading-left-panel-spinner message-loading-left-panel-spinner--small"/>
    }
    return <SelectionMainList isWizard={true}
                              wizardGroup={wizardGroup}
                              useNewWizardFeaturesRequestsLogic={useNewWizardFeaturesRequestsLogic}
                              showListForce={useNewWizardFeaturesRequestsLogic && alreadyRequestedWizardFeatures}
                              currentGroups={currentGroups}
                              setRef={setRef}
                              currentParent={currentParent}
                              features={features}
                              listItemHeight={itemHeight}
                              handleFeatureClick={handleFeatureClick}
      // showItemsList={!this.props.searchRequest && this.state.showItemsList}
                              showItemsList={!resizing}
                              handleUserLoginClick={handleUserLoginClick}
                              layoutName={layoutName}
                              checkIfNeedToSwitchRoom={checkIfNeedToSwitchRoom}
                              parentUpdate={() => updateNavigationCount()}
    />
  }

  return (
    <div className="bv-item-container" ref={itemsList}>
      {getContent()}
    </div>
  )

};

MaterialsWizardMainList.propTypes = {
  currentGroups: PropTypes.arrayOf(PropTypes.shape(wizardGroupInterface)),
  wizardGroup: PropTypes.shape(wizardGroupInterface),
}

const mapStateToProps = (store) => {
  const {wizardList, wizardActiveItem, wizardSelectedValues, wizardActiveStep, wizardMissingFeatures} = store.wizardState;
  const {selectionFeatureInfo, selectionWindowView} = store.selectionState;
  const {filterGroups} = store.filterState;
  const {isMobile, visualizerIsChanging} = store.serverState;
  const {bvOpened} = store.bathroomVisualizerState;
  const {settings} = store.fabricatorState;
  const {userIsLoggedIn, isPhoneNumberValidated} = store.userState;

  return {
    wizardList,
    wizardActiveItem,
    wizardSelectedValues,
    wizardActiveStep,
    selectionFeatureInfo,
    filterGroups,
    selectionWindowView,
    isMobile,
    bvOpened,
    wizardMissingFeatures,
    fabricatorSettings: settings,
    visualizerIsChanging,
    isPhoneNumberValidated,
    userIsLoggedIn,
  };
};

const mapDispatchToProps = {
  setWizardSelectedValues,
  updateNavigationCount,
  toggleUserPopup,
};

export default connect(mapStateToProps, mapDispatchToProps)(MaterialsWizardMainList)
